// Alpine timing fix: defer scripts run before DOMContentLoaded; Alpine checks
// readyState on its own defer script — if 'interactive' it calls start() immediately,
// before body defer scripts (widget_js files) have run. Return 'loading' so Alpine
// registers a DOMContentLoaded listener instead, giving widget scripts time to execute.
;(function () {
    let _suspended = true;
    const _native = Object.getOwnPropertyDescriptor(Document.prototype, 'readyState').get;
    Object.defineProperty(document, 'readyState', {
        get() { return _suspended ? 'loading' : _native.call(this); },
        configurable: true,
    });
    document.addEventListener('DOMContentLoaded', () => {
        _suspended = false;
    }, { once: true });
})();

/**
 * @fileoverview Universal Alpine.js component factories.
 *
 * Each export is an `Alpine.data()` registration — a factory function that
 * returns a plain object Alpine turns into a reactive component. Spread the
 * `pathUtilsMixin()` helper into any factory that needs active-path detection.
 *
 * Registered components
 * ─────────────────────
 *  navLink            Simple link with active-path highlighting.
 *  dropdown           Click / hover dropdown with optional parent-active check.
 *  scrollableDropdown Dropdown panel with overflow-scroll detection & auto-scroll.
 *  accordion          Vertical accordion toggle (mobile-style) with optional parent-active check.
 *  stickyHeader       Header that becomes position-fixed after the user scrolls past it.
 */

document.addEventListener('alpine:init', () => {

    // ─── Path utility mixin ────────────────────────────────────────────────────

    /**
     * Returns a plain object of path-matching helpers.
     * Spread this (`...pathUtilsMixin()`) into any Alpine.data factory that
     * needs to know whether a menu item corresponds to the current page.
     *
     * @returns {{
     *   normalizePath: (path: string) => string,
     *   getCurrentPath: () => string,
     *   isExactMatch:   (itemPath: string) => boolean,
     *   isActiveItem:   (itemPath: string) => boolean
     * }}
     */
    function pathUtilsMixin() {
        return {
            /**
             * Strips query-string and trailing slashes; returns '/' for the root.
             * @param {string} path
             * @returns {string}
             */
            normalizePath(path) {
                const cleaned = path.split('?')[0].replace(/\/+$/, '');
                return cleaned || '/';
            },

            /**
             * Returns the current page pathname, normalized.
             * @returns {string}
             */
            getCurrentPath() {
                return this.normalizePath(window.location.pathname);
            },

            /**
             * Exact match only — does NOT treat sub-paths as active.
             * Use this for items that share a common ancestor (e.g. /shop vs /shop/sale).
             * @param {string} itemPath
             * @returns {boolean}
             */
            isExactMatch(itemPath) {
                return this.getCurrentPath() === this.normalizePath(itemPath);
            },

            /**
             * Returns true when the current page is at or under `itemPath`.
             * e.g. /artists/123 is active for /artists, but / is never a prefix match.
             * @param {string} itemPath
             * @returns {boolean}
             */
            isActiveItem(itemPath) {
                const clean   = this.normalizePath(itemPath);
                const current = this.getCurrentPath();
                if (current === clean) return true;
                if (current === '/' || clean === '/') return false;
                return current.startsWith(clean + '/');
            }
        };
    }

    // ─── navLink ──────────────────────────────────────────────────────────────

    /**
     * Alpine component for a plain anchor that highlights when its path is active.
     *
     * Usage:
     *   <a href="/foo" x-data="navLink()" :class="{ active: isActiveItem('/foo') }">…</a>
     *
     * @returns {ReturnType<pathUtilsMixin>}
     */
    Alpine.data('navLink', () => ({
        ...pathUtilsMixin()
    }));

    // ─── dropdown ─────────────────────────────────────────────────────────────

    /**
     * Alpine component for a click / hover dropdown with optional parent-active detection.
     *
     * Usage:
     *   <div x-data="dropdown(false, ['/about', '/team'])"
     *        @mouseenter="showDropdown()" @mouseleave="hideDropdown()">
     *     <button @click="toggleDropdown()">Menu</button>
     *     <div x-show="open">…</div>
     *   </div>
     *
     * @param {boolean}  [enableHover=false] - Open on hover in addition to click.
     * @param {string[]} [subPaths=[]]       - Child paths used by isParentActive().
     * @returns {{
     *   open:           boolean,
     *   hoverTimeout:   ReturnType<typeof setTimeout>|null,
     *   enableHover:    boolean,
     *   isParentActive: () => boolean,
     *   toggleDropdown: () => void,
     *   showDropdown:   () => void,
     *   hideDropdown:   () => void
     * } & ReturnType<pathUtilsMixin>}
     */
    Alpine.data('dropdown', (enableHover = false, subPaths = []) => ({
        open: false,
        rendered: false,
        /** @type {ReturnType<typeof setTimeout>|null} */
        hoverTimeout: null,
        enableHover,
        ...pathUtilsMixin(),

        /**
         * Returns true when any child path is active on the current page.
         * @returns {boolean}
         */
        isParentActive() {
            return subPaths.some(path => this.isActiveItem(path));
        },

        /** Toggle open/closed; cancels any pending hover-close timer. */
        toggleDropdown() {
            this.open = !this.open;
            if (this.open) this.rendered = true;
            if (this.hoverTimeout) { clearTimeout(this.hoverTimeout); this.hoverTimeout = null; }
        },

        /** Open on hover (no-op when enableHover is false). */
        showDropdown() {
            if (!this.enableHover) return;
            if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
            this.open = true;
            this.rendered = true;
        },

        /** Close after 150 ms on hover-leave (no-op when enableHover is false). */
        hideDropdown() {
            if (!this.enableHover) return;
            this.hoverTimeout = setTimeout(() => { this.open = false; }, 150);
        }
    }));

    // ─── scrollableDropdown ───────────────────────────────────────────────────

    /**
     * Alpine component for a large dropdown panel that detects overflow and
     * exposes auto-scroll helpers for a scroll-indicator button.
     *
     * Expects a `x-ref="content"` on the scrollable inner element.
     *
     * Usage:
     *   <div x-data="scrollableDropdown(true)"
     *        @mouseenter="showDropdown()" @mouseleave="hideDropdown()"
     *        @resize.window.debounce.250ms="checkScrollContent()">
     *     <button @click="toggleMenu()">Menu</button>
     *     <div x-show="open">
     *       <div x-ref="content" @scroll="updateScrollPosition()">…</div>
     *       <button x-show="hasScrollContent && !isAtBottom"
     *               @mousedown="startScrolling()" @mouseup="stopScrolling()">▼</button>
     *     </div>
     *   </div>
     *
     * @param {boolean} [enableHover=false] - Open on hover in addition to click.
     * @returns {{
     *   open:                boolean,
     *   hasScrollContent:    boolean,
     *   isAtBottom:          boolean,
     *   hoverTimeout:        ReturnType<typeof setTimeout>|null,
     *   enableHover:         boolean,
     *   scrollInterval:      ReturnType<typeof setInterval>|null,
     *   scrollSpeed:         number,
     *   toggleMenu:          () => void,
     *   showDropdown:        () => void,
     *   hideDropdown:        () => void,
     *   checkScrollContent:  () => void,
     *   updateScrollPosition:() => void,
     *   startScrolling:      () => void,
     *   stopScrolling:       () => void
     * } & ReturnType<pathUtilsMixin>}
     */
    Alpine.data('scrollableDropdown', (enableHover = false) => ({
        open: false,
        rendered: false,
        hasScrollContent: false,
        isAtBottom: false,
        /** @type {ReturnType<typeof setTimeout>|null} */
        hoverTimeout: null,
        enableHover,
        /** @type {ReturnType<typeof setInterval>|null} */
        scrollInterval: null,
        /** @type {number} Pixels scrolled per animation frame (~16 ms). */
        scrollSpeed: 2,
        ...pathUtilsMixin(),

        /** Toggle panel open/closed; triggers scroll-state recalculation on open. */
        toggleMenu() {
            this.open = !this.open;
            if (this.open) this.rendered = true;
            if (this.hoverTimeout) { clearTimeout(this.hoverTimeout); this.hoverTimeout = null; }
            if (this.open) this.$nextTick(() => { this.checkScrollContent(); this.updateScrollPosition(); });
        },

        /** Open on hover; recalculates scroll state (no-op when enableHover is false). */
        showDropdown() {
            if (!this.enableHover) return;
            if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
            this.open = true;
            this.rendered = true;
            this.$nextTick(() => { this.checkScrollContent(); this.updateScrollPosition(); });
        },

        /** Close after 150 ms on hover-leave (no-op when enableHover is false). */
        hideDropdown() {
            if (!this.enableHover) return;
            this.hoverTimeout = setTimeout(() => { this.open = false; }, 150);
        },

        /**
         * Sets `hasScrollContent` — true when the panel content overflows its container.
         * Bind to `@resize.window.debounce` and call after open.
         */
        checkScrollContent() {
            const c = this.$refs.content;
            if (c) this.hasScrollContent = c.scrollHeight > c.clientHeight;
        },

        /**
         * Updates `isAtBottom` — true when the user has scrolled within 10 px of the bottom.
         * Bind to the `@scroll` event on `x-ref="content"`.
         */
        updateScrollPosition() {
            const c = this.$refs.content;
            if (c) this.isAtBottom = c.scrollHeight - c.scrollTop - c.clientHeight < 10;
        },

        /**
         * Begins auto-scrolling the panel downward at `scrollSpeed` px / frame.
         * Bind to `@mousedown` on the scroll-indicator button.
         */
        startScrolling() {
            if (this.scrollInterval) return;
            this.scrollInterval = setInterval(() => {
                const c = this.$refs.content;
                if (c && !this.isAtBottom) {
                    c.scrollTop += this.scrollSpeed;
                    this.updateScrollPosition();
                    if (this.isAtBottom) this.stopScrolling();
                }
            }, 16);
        },

        /**
         * Stops auto-scrolling.
         * Bind to `@mouseup` / `@mouseleave` on the scroll-indicator button.
         */
        stopScrolling() {
            if (this.scrollInterval) { clearInterval(this.scrollInterval); this.scrollInterval = null; }
        }
    }));

    // ─── accordion ────────────────────────────────────────────────────────────

    /**
     * Alpine component for a vertical accordion (commonly used in mobile navigation).
     *
     * Usage:
     *   <div x-data="accordion(['/about', '/team'])">
     *     <button @click="dropdownOpen = !dropdownOpen"
     *             :class="{ active: isActiveItem('/about') || isParentActive() }">…</button>
     *     <div x-show="dropdownOpen">…</div>
     *   </div>
     *
     * @param {string[]} [subPaths=[]] - Child paths used by isParentActive().
     * @returns {{
     *   dropdownOpen:   boolean,
     *   isParentActive: () => boolean
     * } & ReturnType<pathUtilsMixin>}
     */
    Alpine.data('accordion', (subPaths = []) => ({
        dropdownOpen: false,
        rendered: false,
        ...pathUtilsMixin(),

        /**
         * Returns true when any child path is active on the current page.
         * @returns {boolean}
         */
        isParentActive() {
            return subPaths.some(path => this.isActiveItem(path));
        }
    }));

    // ─── stickyHeader ─────────────────────────────────────────────────────────

    /**
     * Alpine component for a header that switches from `position: relative` to
     * `position: fixed` once the user scrolls past 10 px.
     *
     * Requires a `x-ref="header"` on the `<header>` element so the component
     * can measure its height and update a sibling spacer div.
     *
     * Usage:
     *   <div x-data="stickyHeader()">
     *     <div :style="scrolled ? 'height:' + headerHeight + 'px' : ''"></div>
     *     <header x-ref="header" :class="scrolled ? 'fixed top-0 left-0 right-0 z-50' : 'relative'">
     *       …
     *     </header>
     *   </div>
     *
     * @returns {{
     *   scrolled:     boolean,
     *   headerHeight: number,
     *   init:         () => void
     * }}
     */
    // ─── productMedia ────────────────────────────────────────────────────────

    Alpine.data('productMedia', (hoverEnabled = false, mainIsVideo = false, hoverIsVideo = false) => ({
        hoverMediaEnabled: hoverEnabled,
        showHoverMedia: false,
        mainMediaIsVideo: mainIsVideo,
        hoverMediaIsVideo: hoverIsVideo,
        enter() {
            this.showHoverMedia = this.hoverMediaEnabled;
            if (this.mainMediaIsVideo) this.$refs.mainVideo?.pause();
            if (this.hoverMediaIsVideo && this.showHoverMedia) this.$refs.hoverVideo?.play();
        },
        leave() {
            this.showHoverMedia = false;
            if (this.mainMediaIsVideo) this.$refs.mainVideo?.pause();
            if (this.hoverMediaIsVideo) this.$refs.hoverVideo?.pause();
        }
    }));

    // ─── stickyHeader ─────────────────────────────────────────────────────────

    Alpine.data('stickyHeader', () => ({
        scrolled: false,
        /** @type {number} Captured height of the header element in pixels. */
        headerHeight: 0,

        init() {
            this.$nextTick(() => { this.headerHeight = this.$refs.header.offsetHeight; });
            window.addEventListener('scroll', () => { this.scrolled = window.scrollY > 10; }, { passive: true });
        }
    }));
});
